import { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Search, Trash2, ArrowLeft, Calendar, User as UserIcon, FileText, ChevronDown, ChevronUp, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtINR = (v) => {
  const n = parseFloat(v);
  if (!isFinite(n)) return '—';
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const ENTITY_LABEL = {
  stores_grn: 'Stores GRN',
  raw_material_grn: 'Raw Material GRN',
};

function DeletedGRNs() {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [source, setSource] = useState('all'); // all / stores / raw_material
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ source, page, limit });
      if (search.trim()) params.set('q', search.trim());
      const res = await axios.get(`${API}/stores/grn/audit/deleted?${params.toString()}`);
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load deleted-GRN audit log');
    } finally {
      setLoading(false);
    }
  }, [API, source, page, limit, search]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6" data-testid="deleted-grns-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trash2 size={22} className="text-rose-600" /> Deleted GRNs Audit Log
            </h1>
            <p className="text-gray-500 mt-1">Every GRN that has ever been deleted from the system — with full snapshot, deleter, and timestamp.</p>
          </div>
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{total}</span> total
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-gray-400" />
          <select
            value={source}
            onChange={(e) => { setSource(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none bg-white"
            data-testid="source-filter"
          >
            <option value="all">All GRN Types</option>
            <option value="stores">Stores GRN only</option>
            <option value="raw_material">Raw Material GRN only</option>
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search size={16} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search GRN id, invoice, party…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setPage(1), fetchAudit())}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
            data-testid="search-input"
          />
          <button
            onClick={() => { setPage(1); fetchAudit(); }}
            className="px-3 py-2 bg-[#1a237e] text-white text-sm rounded-lg hover:bg-[#283593]"
            data-testid="search-btn"
          >
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {loading && <div className="text-center text-gray-500 py-10">Loading…</div>}
        {error && (
          <div className="text-center text-rose-600 py-10" data-testid="audit-error">
            {error}
            <button onClick={fetchAudit} className="ml-3 underline text-[#1a237e]">Retry</button>
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="text-center text-gray-500 py-12" data-testid="audit-empty">
            No deleted GRNs in the audit log yet.
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">GRN Id</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Invoice / Party</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Deleted By</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Deleted On</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => {
                  const od = r.oldDoc || {};
                  const isOpen = !!expanded[r.id];
                  return (
                    <>
                      <tr key={r.id} className="hover:bg-gray-50" data-testid={`audit-row-${r.entityId}`}>
                        <td className="px-4 py-3 font-mono text-sm text-rose-700 font-medium">{r.entityId}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${r.entityType === 'stores_grn' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}`}>
                            {ENTITY_LABEL[r.entityType] || r.entityType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium">{od.invoiceNo || od.invoiceNumber || '—'}</div>
                          <div className="text-xs text-gray-500">{od.partyName || od.supplierName || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-1.5 text-gray-700">
                            <UserIcon size={12} className="text-gray-400" />
                            <span>{r.userName || 'System'}</span>
                          </div>
                          {r.userEmail && <div className="text-xs text-gray-400 ml-4">{r.userEmail}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={12} className="text-gray-400" />
                            <span>{fmtDate(r.timestamp)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => toggle(r.id)}
                            className="text-xs text-[#1a237e] hover:bg-indigo-50 px-2 py-1 rounded flex items-center gap-1 ml-auto"
                            data-testid={`toggle-${r.entityId}`}
                          >
                            {isOpen ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50/60" data-testid={`detail-${r.entityId}`}>
                          <td colSpan={6} className="px-4 py-4">
                            <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                              <FileText size={12} /> Snapshot of the deleted GRN
                            </h4>
                            {(!od || Object.keys(od).length === 0) ? (
                              <p className="text-xs text-gray-500 italic">No snapshot available for this row (deleted before snapshot logging was enabled).</p>
                            ) : (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                <Field label="GRN Id" value={od.id || od.grnNo} mono />
                                <Field label="Invoice" value={od.invoiceNo || od.invoiceNumber} />
                                <Field label="Party / Supplier" value={od.partyName || od.supplierName} />
                                <Field label="GRN Date" value={od.grnDate || od.date} />
                                <Field label="PO" value={od.poNo || od.poNumber} />
                                <Field label="Total Value" value={fmtINR(od.totalAmount || od.netAmount || od.invoiceValue)} />
                                <Field label="Item Count" value={(od.items || []).length || '—'} />
                                <Field label="Created At" value={od.createdAt ? fmtDate(od.createdAt) : '—'} />
                              </div>
                            )}
                            {Array.isArray(od.items) && od.items.length > 0 && (
                              <div className="mt-3 bg-white rounded border border-gray-200 overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-2 py-1 text-left font-semibold text-gray-600">Item</th>
                                      <th className="px-2 py-1 text-right font-semibold text-gray-600">Qty</th>
                                      <th className="px-2 py-1 text-left font-semibold text-gray-600">Unit</th>
                                      <th className="px-2 py-1 text-right font-semibold text-gray-600">Rate</th>
                                      <th className="px-2 py-1 text-right font-semibold text-gray-600">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {od.items.map((it, idx) => (
                                      <tr key={idx}>
                                        <td className="px-2 py-1">{it.itemName || it.name || it.description || '—'}</td>
                                        <td className="px-2 py-1 text-right">{it.qty || it.quantity || it.receivedQty || '—'}</td>
                                        <td className="px-2 py-1">{it.unit || '—'}</td>
                                        <td className="px-2 py-1 text-right">{fmtINR(it.rate)}</td>
                                        <td className="px-2 py-1 text-right">{fmtINR(it.amount || it.lineTotal)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && total > limit && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-sm">
            <span className="text-gray-600">Page {page} of {totalPages} · {total} rows</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded disabled:opacity-50 hover:bg-white"
              >Prev</button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded disabled:opacity-50 hover:bg-white"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="bg-white rounded border border-gray-200 px-2 py-1.5">
      <div className="text-[10px] uppercase text-gray-500 tracking-wide">{label}</div>
      <div className={`text-gray-900 ${mono ? 'font-mono text-xs' : 'text-xs font-medium'}`}>
        {value || '—'}
      </div>
    </div>
  );
}

export default DeletedGRNs;
